/**
 * 视频风格画廊弹窗 — 2026-06-04
 *
 * 31 风格 + 自由发挥,按媒介(实拍/3D/MG/2D/绘画/定格)分组 sticky 标题展示,
 * 顶部搜索按 label/blurb/tone 实时过滤。点 tile 即选并关闭。
 * 取代原 VideoBriefPanel 里 33 颗文字 chip 墙(用户反馈"看不懂那些文字")。
 */
import { useEffect, useMemo, useState } from "react";
import { Search, Sparkles } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";
import { VideoStyleThumb } from "../../ui/components/VideoStyleThumb";
import {
  isCustomVideoTone,
  PRESET_VIDEO_STYLES,
  recommendedVideoStyleKeys,
  VIDEO_MEDIA,
  type VideoStyleOption,
} from "../../ui/components/videoStyleOptions";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** 当前选中 tone(null = 自由发挥) */
  selectedTone: string | null;
  /** 选风格:回传 tone(null=自由发挥)+ key(持久化 styleCode) */
  onSelect: (tone: string | null, key?: string) => void;
  /**
   * 租户行业码(1004 第三轮)。消费向行业:顶部先给「适合你行业」那几张(与风格条 / 军师同口径),
   * 其余风格**折叠**在下面 —— 保留全量(用户真想要可以自己挑),但不摆在眼前误导;搜索时不折叠(搜 = 明确意图)。
   * 选折叠而不是删:删掉等于替用户做主,而画风偶尔确有需要(如节日动画);排后仍会被当成「推荐」。
   */
  industryCode?: string;
}

export function VideoStyleGalleryDialog({
  open,
  onOpenChange,
  selectedTone,
  onSelect,
  industryCode,
}: Props) {
  const [q, setQ] = useState("");
  const recoKeys = recommendedVideoStyleKeys(industryCode);
  const [showOthers, setShowOthers] = useState(false);
  useEffect(() => {
    if (open) setShowOthers(false);
  }, [open]);
  /**
   * ★自定义画风从主面搬进来(2026-08-06 重排 C6)。
   * 重排前主面同时有「选风格 ▸」按钮和一个「或自定义:[____]」输入框 —— **两个入口做同一件事**,
   * 而且那个框永远占着一行,给一个绝大多数人不会用的能力。收进画廊底部:想用的人在这里,
   * 不用的人在主面一眼看不到它。开弹层时回显当前自定义值(不是预设就是自定义)。
   */
  const [customDraft, setCustomDraft] = useState("");
  useEffect(() => {
    if (open) setCustomDraft(isCustomVideoTone(selectedTone) ? (selectedTone ?? "") : "");
  }, [open, selectedTone]);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return PRESET_VIDEO_STYLES;
    return PRESET_VIDEO_STYLES.filter(
      (s) =>
        s.label.toLowerCase().includes(kw) ||
        (s.blurb || "").toLowerCase().includes(kw) ||
        (s.tone || "").toLowerCase().includes(kw)
    );
  }, [q]);

  const pick = (tone: string | null, key?: string) => {
    onSelect(tone, key);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[82vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-2 border-b px-5 py-4">
          <DialogTitle>选择视频风格</DialogTitle>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="搜索风格(名称 / 关键词,如:电影、3D、水墨、撞色)"
              className="pl-9"
              aria-label="搜索视频风格"
            />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!q.trim() && (
            <button
              type="button"
              onClick={() => pick(null)}
              aria-pressed={selectedTone === null}
              className={cn(
                "mb-5 flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                selectedTone === null
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border/60 hover:border-primary/50"
              )}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Sparkles className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-medium text-foreground">自由发挥</span>
                <span className="block text-xs text-muted-foreground">
                  不指定风格,由 AI 根据你的内容与目的自动选媒介与调性
                </span>
              </span>
            </button>
          )}

          {recoKeys && !q.trim() && (
            <section className="mb-6" data-testid="video-style-reco">
              <div className="sticky top-0 z-10 -mx-5 mb-3 bg-background/95 px-5 py-1 backdrop-blur">
                <h3 className="text-sm font-semibold text-foreground">
                  适合你行业的风格
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    真实质感、贴近产品本身
                  </span>
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {recoKeys
                  .map((k) => PRESET_VIDEO_STYLES.find((s) => s.key === k))
                  .filter((s): s is VideoStyleOption => !!s)
                  .map((s) => (
                    <VideoStyleThumb
                      key={s.key ?? s.label}
                      option={s}
                      selected={selectedTone === s.tone}
                      onSelect={() => pick(s.tone, s.key)}
                      size="md"
                    />
                  ))}
              </div>
              <button
                type="button"
                data-testid="video-style-others-toggle"
                onClick={() => setShowOthers((v) => !v)}
                className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
              >
                {showOthers
                  ? "收起其它风格"
                  : `其它风格(${PRESET_VIDEO_STYLES.length - recoKeys.length} 种,偏动漫/科幻/潮玩,拍实物容易跑偏)`}
              </button>
            </section>
          )}

          {VIDEO_MEDIA.map((m) => {
            // 消费向行业 + 未搜索 + 未展开:其余风格折叠不渲染;展开后推荐那几张不重复出现
            if (recoKeys && !q.trim() && !showOthers) return null;
            const items = filtered.filter(
              (s) =>
                !(recoKeys && !q.trim() && s.key && recoKeys.includes(s.key)) && s.medium === m.key
            );
            if (items.length === 0) return null;
            return (
              <section key={m.key} className="mb-6 last:mb-0">
                <div className="sticky top-0 z-10 -mx-5 mb-3 bg-background/95 px-5 py-1 backdrop-blur">
                  <h3 className="text-sm font-semibold text-foreground">
                    {m.title}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">{m.hint}</span>
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {items.map((s: VideoStyleOption) => (
                    <VideoStyleThumb
                      key={s.key ?? s.label}
                      option={s}
                      selected={selectedTone === s.tone}
                      onSelect={() => pick(s.tone, s.key)}
                      size="md"
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {q.trim() && filtered.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              没有匹配「{q.trim()}」的风格,换个词,或用下面的「自己描述画风」直接写。
            </p>
          )}
        </div>

        {/* 自己描述画风:预设里没有想要的那种时的出口(见 customDraft 处注释)。 */}
        <div className="flex items-center gap-2 border-t px-5 py-3">
          <label htmlFor="video-style-custom" className="shrink-0 text-2xs text-muted-foreground">
            自己描述画风
          </label>
          <Input
            id="video-style-custom"
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && customDraft.trim()) pick(customDraft.trim());
            }}
            className="h-8 text-xs"
            placeholder="如:赛博朋克霓虹、莫兰迪色手绘…"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 shrink-0 text-xs"
            disabled={!customDraft.trim()}
            onClick={() => pick(customDraft.trim())}
          >
            用这个
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
